import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import type { RootState } from "@/store"
import { useAppDispatch } from "@/hooks/useAppDispatch"
import { changeLayoutMode } from "@/slices/thunks"
import { LayoutModeTypes } from "@/enums/layout"
import useUser from "@/hooks/useUser"
import { rutaPermitida, rutasDelMenu } from "@/routes/menuRoutes"
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "@celink/ui"
import {
  Clapperboard,
  LayoutDashboard,
  Home,
  ShoppingCart,
  UserPlus,
  Users,
  UserCog,
  Search,
  Hash,
  ArrowLeftRight,
  Repeat,
  Tag,
  Percent,
  Image,
  Package,
  Boxes,
  Smartphone,
  Building2,
  Building,
  FileText,
  Receipt,
  Layers,
  MessageSquare,
  MessageSquareText,
  User,
  Moon,
  Sun,
  CardSim,
} from "lucide-react"

type Cmd = { label: string; path: string; icon: React.ElementType; keywords?: string }

// Navegación real (src/routes/allRoutes.tsx), agrupada por dominio.
const GROUPS: { heading: string; items: Cmd[] }[] = [
  {
    heading: "General",
    items: [
      { label: "Dashboard", path: "/", icon: LayoutDashboard, keywords: "inicio home tablero" },
      { label: "Inicio", path: "/inicio", icon: Home, keywords: "welcome bienvenida" },
      { label: "Ventas", path: "/ventas", icon: ShoppingCart, keywords: "sales recargas" },
      { label: "Leads", path: "/leads", icon: UserPlus, keywords: "prospectos embudo" },
    ],
  },
  {
    heading: "Clientes y líneas",
    items: [
      { label: "Clientes", path: "/clientes", icon: Users },
      { label: "Usuarios", path: "/usuarios", icon: UserCog, keywords: "staff permisos" },
      { label: "Consulta UF", path: "/consulta-uf", icon: Search, keywords: "perfil linea msisdn" },
      { label: "MSISDN", path: "/msisdn", icon: Hash, keywords: "numero inventario sim" },
      { label: "Operaciones", path: "/portabilidad", icon: ArrowLeftRight, keywords: "portabilidad porta-in porta-out activar recargar cambio region" },
      { label: "Gestión de portabilidades", path: "/portabilidades", icon: Repeat },
      { label: "Beneficios", path: "/beneficios", icon: Clapperboard, keywords: "pluus cine 2x1 asistencias membresias boletos be protected" },
    ],
  },
  {
    heading: "Catálogo y comercial",
    items: [
      { label: "Ofertas", path: "/ofertas", icon: Tag, keywords: "planes paquetes offers" },
      { label: "Promociones", path: "/promociones", icon: Percent, keywords: "bonos portabilidad campañas matriz" },
      { label: "Catálogo Altán", path: "/promociones-altan", icon: Tag, keywords: "offerings detectados freeunit" },
      { label: "Banners", path: "/banners", icon: Image, keywords: "ribbon app promocional" },
      { label: "Órdenes ecommerce", path: "/ecommerce", icon: Package, keywords: "tienda pedidos" },
      { label: "Productos ecommerce", path: "/ecommerce-products", icon: Boxes },
      { label: "Perfiles eSIM", path: "/esim-profiles", icon: Smartphone, keywords: "qr esim" },
    ],
  },
  {
    heading: "Empresas y marca",
    items: [
      { label: "Empresas", path: "/empresas", icon: Building2, keywords: "flotillas b2b" },
      { label: "Datos de empresa", path: "/datos-empresa", icon: Building, keywords: "fiscales rfc" },
      { label: "Familias", path: "/familias", icon: Layers, keywords: "family_type perfiles de precio segmentos sims" },
    ],
  },
  {
    heading: "Finanzas",
    items: [
      { label: "Facturación", path: "/facturacion", icon: FileText, keywords: "cfdi contabilidad" },
      { label: "Liquidaciones", path: "/liquidaciones", icon: Receipt, keywords: "pagos comisiones" },
      { label: "Batch", path: "/batch", icon: Layers, keywords: "lote procesos" },
      { label: "Preactivación", path: "/preactivacion", icon: CardSim, keywords: "preregistro sim caja lote altan" },
    ],
  },
  {
    heading: "Mensajería",
    items: [
      { label: "Campañas SMS", path: "/sms-campaigns", icon: MessageSquare },
      { label: "Plantillas SMS", path: "/sms-templates", icon: MessageSquareText },
    ],
  },
  {
    heading: "Cuenta",
    items: [{ label: "Perfil", path: "/perfil", icon: User, keywords: "mi cuenta ajustes" }],
  },
]

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  // Sólo las pantallas a las que el menú del usuario deja entrar: antes listaba todas y la ajena
  // rebotaba en silencio a la primera pantalla permitida.
  const { menu } = useUser()
  const grupos = useMemo(() => {
    const rutas = rutasDelMenu(menu)
    return GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => rutaPermitida(i.path, rutas)) })).filter(
      (g) => g.items.length > 0,
    )
  }, [menu])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  // Tema desde la fuente única (redux). El toggle rutea por changeLayoutMode,
  // que sincroniza data-bs-theme + sidebar/topbar; isDark es reactivo al store.
  const layoutMode = useSelector((s: RootState) => s.Layout.layoutModeType)
  const isDark = layoutMode === LayoutModeTypes.DARKMODE

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [])

  const go = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Buscar página o acción…  (⌘K / Ctrl+K)" />
      <CommandList>
        <CommandEmpty>Sin resultados.</CommandEmpty>
        {grupos.map((group) => (
          <CommandGroup key={group.heading} heading={group.heading}>
            {group.items.map((item) => {
              const Icon = item.icon
              return (
                <CommandItem
                  key={item.path}
                  value={`${item.label} ${item.keywords ?? ""} ${item.path}`}
                  onSelect={() => go(item.path)}
                >
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{item.label}</span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        ))}
        <CommandGroup heading="Preferencias">
          <CommandItem
            value="tema modo oscuro claro dark light"
            onSelect={() => {
              dispatch(
                changeLayoutMode(
                  isDark ? LayoutModeTypes.LIGHTMODE : LayoutModeTypes.DARKMODE,
                ),
              )
              setOpen(false)
            }}
          >
            {isDark ? (
              <Sun className="mr-2 h-4 w-4 text-muted-foreground" />
            ) : (
              <Moon className="mr-2 h-4 w-4 text-muted-foreground" />
            )}
            <span>Cambiar a modo {isDark ? "claro" : "oscuro"}</span>
            <CommandShortcut>tema</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}

export default CommandPalette
